// src/components/HODDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import StaffSidebar from "../components/StaffSidebar";
import Modal from "react-modal";
import "../assets/css/staff-dashboard.css";
import HODSidebar from "../components/HODSidebar";

Modal.setAppElement("#root");

export default function StaffDashboard() {
  const programme = localStorage.getItem("programme");
  const API =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_BASE_URL || "https://schools.luth.gov.ng"
      : "http://localhost:3003";

  const PAGE_SIZE = 12;
  const LEVELS = [
    "All",
    "ND1",
    "ND2",
    "HND1",
    "HND2",
    "Postgraduate",
    "Diploma",
    "Higher Diploma",
  ];
  const CARD_LEVELS = LEVELS.filter((lvl) => lvl !== "All");

  const isCommunityHealth =
    programme?.trim().toLowerCase() === "community health";

  /*
   * Community Health staff can switch between:
   * - All Community Health
   * - Community Health I
   * - Community Health II
   *
   * Other programmes only see their own programme.
   */
  const programmeOptions = useMemo(() => {
    if (isCommunityHealth) {
      return [
        "All Community Health",
        "Community Health I",
        "Community Health II",
      ];
    }

    return programme ? [programme] : [];
  }, [programme, isCommunityHealth]);

  const [selectedProgramme, setSelectedProgramme] = useState(
    isCommunityHealth ? "All Community Health" : programme || "",
  );

  /*
   * Make sure the filter resets correctly when the logged-in
   * programme changes.
   */
  useEffect(() => {
    if (isCommunityHealth) {
      setSelectedProgramme("All Community Health");
    } else {
      setSelectedProgramme(programme || "");
    }
  }, [programme, isCommunityHealth]);

  /*
   * Programme(s) that should actually be sent to the database.
   */
  const programmesToFetch = useMemo(() => {
    if (!programme) return [];

    if (!isCommunityHealth) {
      return [programme];
    }

    if (selectedProgramme === "Community Health I") {
      return ["Community Health I"];
    }

    if (selectedProgramme === "Community Health II") {
      return ["Community Health II"];
    }

    // All Community Health
    return ["Community Health I", "Community Health II"];
  }, [programme, isCommunityHealth, selectedProgramme]);

  // summary cards
  const [totalStudents, setTotalStudents] = useState(0);
  const [byLevel, setByLevel] = useState([]);

  // students panel
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [studentLevel, setStudentLevel] = useState("All");
  const [studentPage, setStudentPage] = useState(1);

  // courses panel
  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [courseLevel, setCourseLevel] = useState("All");
  const [coursePage, setCoursePage] = useState(1);

  // profile modal
  const [modalOpen, setModalOpen] = useState(false);
  const [profile, setProfile] = useState(null);

  const handleViewProfile = async (id) => {
    try {
      const { data } = await axios.get(`${API}/api/student/${id}`);
      setProfile(data);
      setModalOpen(true);
    } catch (err) {
      console.error(err);
      alert("Failed to load profile");
    }
  };

  // fetch summary
  useEffect(() => {
    if (!programmesToFetch.length) return;

    const fetchSummary = async () => {
      try {
        // --------------------------------------------
        // TOTAL STUDENTS
        // --------------------------------------------

        const countResponses = await Promise.all(
          programmesToFetch.map((prog) =>
            axios.get(`${API}/api/staff/students/count`, {
              params: {
                programme: prog,
              },
            }),
          ),
        );

        const total = countResponses.reduce((sum, response) => {
          return sum + Number(response.data?.count || 0);
        }, 0);

        setTotalStudents(total);

        // --------------------------------------------
        // COUNT BY LEVEL
        // --------------------------------------------

        const levelResponses = await Promise.all(
          programmesToFetch.map((prog) =>
            axios.get(`${API}/api/staff/students/countByLevel`, {
              params: {
                programme: prog,
              },
            }),
          ),
        );

        const mergedLevels = {};

        levelResponses.forEach((response) => {
          const data = Array.isArray(response.data)
            ? response.data
            : [];

          data.forEach((item) => {
            const Level = item.Level;
            const count = Number(item.count || 0);

            if (!mergedLevels[Level]) {
              mergedLevels[Level] = 0;
            }

            mergedLevels[Level] += count;
          });
        });

        const formattedLevels = Object.entries(mergedLevels).map(
          ([Level, count]) => ({
            Level,
            count,
          }),
        );

        setByLevel(formattedLevels);
      } catch (err) {
        console.error("Failed to fetch summary:", err);

        setTotalStudents(0);
        setByLevel([]);
      }
    };

    fetchSummary();
  }, [API, programmesToFetch]);

  // fetch students
  useEffect(() => {
    if (!programmesToFetch.length) return;

    const fetchStudents = async () => {
      try {
        const responses = await Promise.all(
          programmesToFetch.map((prog) =>
            axios.get(`${API}/api/staff/students`, {
              params: {
                programme: prog,
                level:
                  studentLevel === "All"
                    ? ""
                    : studentLevel,
                search: studentSearch.trim(),
              },
            }),
          ),
        );

        const mergedStudents = responses.reduce(
          (allStudents, response) => {
            const data = Array.isArray(response.data)
              ? response.data
              : [];

            return [...allStudents, ...data];
          },
          [],
        );

        setStudents(mergedStudents);
        setStudentPage(1);
      } catch (err) {
        console.error("Failed to fetch students:", err);
        setStudents([]);
      }
    };

    fetchStudents();
  }, [
    API,
    programmesToFetch,
    studentLevel,
    studentSearch,
  ]);

  // fetch courses
  useEffect(() => {
    if (!programmesToFetch.length) return;

    const fetchCourses = async () => {
      try {
        const responses = await Promise.all(
          programmesToFetch.map((prog) =>
            axios.get(`${API}/api/staff/courses`, {
              params: {
                programme: prog,
                level:
                  courseLevel === "All"
                    ? ""
                    : courseLevel,
                search: courseSearch.trim(),
              },
            }),
          ),
        );

        const mergedCourses = responses.reduce(
          (allCourses, response) => {
            const data = Array.isArray(response.data)
              ? response.data
              : [];

            return [...allCourses, ...data];
          },
          [],
        );

        /*
         * Remove duplicate courses if the same course exists
         * in more than one returned result.
         */
        const uniqueCourses = Array.from(
          new Map(
            mergedCourses.map((course) => [
              course.CourseID,
              course,
            ]),
          ).values(),
        );

        setCourses(uniqueCourses);
        setCoursePage(1);
      } catch (err) {
        console.error("Failed to fetch courses:", err);
        setCourses([]);
      }
    };

    fetchCourses();
  }, [
    API,
    programmesToFetch,
    courseLevel,
    courseSearch,
  ]);

  // pagination
  const studentPages = Math.ceil(
    students.length / PAGE_SIZE,
  );

  const coursePages = Math.ceil(
    courses.length / PAGE_SIZE,
  );

  const pagedStudents = students.slice(
    (studentPage - 1) * PAGE_SIZE,
    studentPage * PAGE_SIZE,
  );

  const pagedCourses = courses.slice(
    (coursePage - 1) * PAGE_SIZE,
    coursePage * PAGE_SIZE,
  );

  return (
    <div
      className="staff-dashboard"
      style={{ marginTop: "-100px" }}
    >
      <StaffSidebar />

      <main>
        <h1>Staff Dashboard</h1>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card total">
            <h2>Total Students</h2>
            <p className="big-number">
              {totalStudents}
            </p>
          </div>

          {CARD_LEVELS.map((lvl) => {
            const entry =
              byLevel.find((b) => b.Level === lvl) || {
                count: 0,
              };

            return (
              <div
                key={lvl}
                className="summary-card"
              >
                <h4>{lvl}</h4>

                <p className="small-number">
                  {entry.count}
                </p>
              </div>
            );
          })}
        </div>

        {/* Panels */}
        <div className="two-column modern">
          {/* Students Panel */}
          <section className="panel">
            <h3>Students</h3>

            <div className="controls">
              {/* Community Health Programme Filter */}
              {isCommunityHealth && (
                <select
                  value={selectedProgramme}
                  onChange={(e) => {
                    setSelectedProgramme(
                      e.target.value,
                    );

                    setStudentPage(1);
                    setCoursePage(1);
                  }}
                >
                  {programmeOptions.map((prog) => (
                    <option
                      key={prog}
                      value={prog}
                    >
                      {prog}
                    </option>
                  ))}
                </select>
              )}

              <input
                placeholder="Search…"
                value={studentSearch}
                onChange={(e) =>
                  setStudentSearch(e.target.value)
                }
              />

              <select
                value={studentLevel}
                onChange={(e) =>
                  setStudentLevel(e.target.value)
                }
              >
                {LEVELS.map((lvl) => (
                  <option
                    key={lvl}
                    value={lvl}
                  >
                    {lvl}
                  </option>
                ))}
              </select>
            </div>

            <ul className="list modern">
              {pagedStudents.map((s) => (
                <li key={s.id}>
                  <div>
                    <strong>
                      {s.fullName}
                    </strong>{" "}
                    <span>
                      ({s.matricNumber})
                    </span>
                  </div>

                  <div>
                    <span className="lvl-tag">
                      {s.Level}
                    </span>

                    <button
                      className="btn-view"
                      onClick={() =>
                        handleViewProfile(s.id)
                      }
                    >
                      View
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <Pagination
              page={studentPage}
              total={studentPages}
              onChange={setStudentPage}
            />
          </section>

          {/* Courses Panel */}
          <section className="panel">
            <h3>Courses</h3>

            <div className="controls">
              {/* Community Health Programme Filter */}
              {isCommunityHealth && (
                <select
                  value={selectedProgramme}
                  onChange={(e) => {
                    setSelectedProgramme(
                      e.target.value,
                    );

                    setStudentPage(1);
                    setCoursePage(1);
                  }}
                >
                  {programmeOptions.map((prog) => (
                    <option
                      key={prog}
                      value={prog}
                    >
                      {prog}
                    </option>
                  ))}
                </select>
              )}

              <input
                placeholder="Search…"
                value={courseSearch}
                onChange={(e) =>
                  setCourseSearch(e.target.value)
                }
              />

              <select
                value={courseLevel}
                onChange={(e) =>
                  setCourseLevel(e.target.value)
                }
              >
                {LEVELS.map((lvl) => (
                  <option
                    key={lvl}
                    value={lvl}
                  >
                    {lvl}
                  </option>
                ))}
              </select>
            </div>

            <table className="table modern">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Title</th>
                  <th>Level</th>
                  <th>Units</th>
                </tr>
              </thead>

              <tbody>
                {pagedCourses.map((c) => (
                  <tr key={c.CourseID}>
                    <td>{c.CourseCode}</td>

                    <td>{c.CourseTitle}</td>

                    <td>{c.Level}</td>

                    <td>
                      {c.CreditUnits ?? "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              page={coursePage}
              total={coursePages}
              onChange={setCoursePage}
            />
          </section>
        </div>

        {/* Profile Modal */}
        <Modal
          isOpen={modalOpen}
          onRequestClose={() =>
            setModalOpen(false)
          }
          className="modal modern"
          overlayClassName="overlay"
        >
          <button
            className="modal-close"
            onClick={() =>
              setModalOpen(false)
            }
          >
            ×
          </button>

          {profile ? (
            <div className="profile">
              <h2>{profile.fullName}</h2>

              <p>
                <strong>
                  Matric No:
                </strong>{" "}
                {profile.matricNumber}
              </p>

              <p>
                <strong>Level:</strong>{" "}
                {profile.Level}
              </p>

              <p>
                <strong>
                  Department:
                </strong>{" "}
                {profile.department}
              </p>

              <p>
                <strong>Email:</strong>{" "}
                {profile.email}
              </p>

              <p>
                <strong>Phone:</strong>{" "}
                {profile.phoneNumber}
              </p>
            </div>
          ) : (
            <p>Loading…</p>
          )}
        </Modal>
      </main>
    </div>
  );
}

function Pagination({
  page,
  total,
  onChange,
}) {
  if (total <= 1) return null;

  return (
    <div className="pagination modern">
      <button
        disabled={page === 1}
        onClick={() =>
          onChange(page - 1)
        }
      >
        ‹
      </button>

      {[...Array(total)].map((_, i) => (
        <button
          key={i}
          className={
            i + 1 === page
              ? "active"
              : ""
          }
          onClick={() =>
            onChange(i + 1)
          }
        >
          {i + 1}
        </button>
      ))}

      <button
        disabled={page === total}
        onClick={() =>
          onChange(page + 1)
        }
      >
        ›
      </button>
    </div>
  );
}

